import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../state/store";
import { useAppDispatch } from "../../state/hooks";
import { useSignalRContext } from "../Contexts/SignalRContext";
import { loadSessionInfo } from "../../state/actions/authActions";
import { savePendingLanguage } from "../../i18n/languageChoice";

function refreshUserInfo(dispatch: AppDispatch, userId: string | undefined) {
  savePendingLanguage(userId)
    .then(() => dispatch(loadSessionInfo()))
    .catch(() => {
      // Offline, the stored user is the best available.
    });
}

export default function UserInfoRefreshListener() {
  const dispatch = useAppDispatch();
  const { connection, isReconnecting } = useSignalRContext();
  const isAuthenticated = useSelector((s: RootState) => s.authReducer.isAuthenticated);
  const userId = useSelector((s: RootState) => s.authReducer.user?.id);
  const [isRestoredSession] = useState(isAuthenticated);
  const isHubLive = !!connection && !isReconnecting;
  const hubWasLive = useRef(false);
  const refresh = useEffectEvent(() => refreshUserInfo(dispatch, userId));

  useEffect(() => {
    if (isRestoredSession) refresh();
  }, [isRestoredSession]);

  useEffect(() => {
    if (!isAuthenticated) {
      hubWasLive.current = false;
      return;
    }
    if (!isHubLive) return;

    if (hubWasLive.current) refresh();
    hubWasLive.current = true;
  }, [isAuthenticated, isHubLive]);

  useEffect(() => {
    if (!connection || !isAuthenticated) return;

    const handleUserInfoChanged = () => refresh();

    connection.on("UserInfoChangedAsync", handleUserInfoChanged);

    return () => {
      connection.off("UserInfoChangedAsync", handleUserInfoChanged);
    };
  }, [connection, isAuthenticated]);

  return null;
}
